import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Alert, Button, Row, Layout } from 'antd';
import { DragDropContext } from 'react-beautiful-dnd';
import ListOfCertifications from './ListOfCertifications.js';
import CertificationCSPModalForm from './CertificationCSPModalForm.js';
import dndStructure from './initStructure/dndStructure.js';
import sampleCertifications from './initStructure/SampleCertifications.js';
import {checkColumnStructure, checkCertsStructure, verifyCertValues} from './utils.js';
import SelectedCertifications from './SelectedCertifications.js';
import Statistics from './Statistics.js';
import CertificationBrowseCSPModalForm from './CertificationBrowseCSPModalForm.js';

const { Content } = Layout;

const Certifications = ({ dbCertifications, rsSkillsGroup, rsSpecificKnowledge, rsSpecificSkills, rsProfiles, dbDomains, dbECSFRoles }) => {
  const [certifications, setCertifications] = useState({});
  const [columns, setColumns] = useState(dndStructure.columns);
  const [error, setError] = useState(null);
  const [draggedCertification, setDraggedCertification] = useState(null);
  const [sampleLoaded, setSampleLoaded] = useState(false);
  const [showBrowseModal, setShowBrowseModal] = useState(false);
  const [addModalData, setAddModalData] = useState(null);
  const [modalCertificationId, setModalCertificationId] = useState(null);

  const initialColumns = useMemo(() => JSON.parse(JSON.stringify(dndStructure.columns)), []);

  const location = useLocation();
  useEffect(() => {
    setColumns(initialColumns);
  }, [location.pathname, initialColumns]);

  const openAddModal = (id) => {
    setModalCertificationId(id);
    setAddModalData(id ? certifications[id] : 'new');
  };

  const closeAddModal = () => {
    setAddModalData(null);
    setModalCertificationId(null);
  };

  const openBrowseModal = () => setShowBrowseModal(true);
  const closeBrowseModal = () => setShowBrowseModal(false);

  const createDnDId = (name, prefix, pool) => {
    let base = `${prefix}_${name.replace(/ /g, '_').toLowerCase()}`;
    let i = 0;
    while (pool.includes(`${base}_${i}`)) i++;
    return `${base}_${i}`;
  };

  const submitBrowse = (selectedIds) => {
    let newCols = { ...columns };
    let newCerts = { ...certifications };

    selectedIds.forEach((id) => {
      const item = dbCertifications.find((i) => i.id === id);
      if (!item) return;
      const copy = JSON.parse(JSON.stringify(item));
      copy.id = createDnDId(copy.name, 'db', Object.keys(newCerts));
      newCols.available.certificationIds.push(copy.id);
      newCerts[copy.id] = copy;
    });

    setColumns(newCols);
    setCertifications(newCerts);
    closeBrowseModal();
  };

  const submitModal = (inputData) => {
    if (modalCertificationId) updateCert(inputData);
    else addCustomCert(inputData);
    closeAddModal();
  };

  const addCustomCert = (input) => {
    const copy = { ...input };
    copy.id = createDnDId(copy.name, 'user', Object.keys(certifications));
    setCertifications((prev) => ({ ...prev, [copy.id]: copy }));
    setColumns((prev) => ({
      ...prev,
      available: {
        ...prev.available,
        certificationIds: [...prev.available.certificationIds, copy.id],
      },
    }));
  };
  const updateCert = (input) => {
    if (!modalCertificationId) return;
    setCertifications((prev) => ({
      ...prev,
      [modalCertificationId]: { ...prev[modalCertificationId], ...input, id: modalCertificationId },
    }));
  };

  const removeForeverCert = ({ id, position }) => {
    setColumns((prev) => ({
      ...prev,
      [position]: {
        ...prev[position],
        certificationIds: prev[position].certificationIds.filter((x) => x !== id),
      },
    }));
    setCertifications((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };

  const removeCert = ({ id, position }) => {
    setColumns((prev) => {
      const from = [...prev[position].certificationIds];
      const to = [...prev.available.certificationIds, id];
      return {
        ...prev,
        [position]: { ...prev[position], certificationIds: from.filter((x) => x !== id) },
        available: { ...prev.available, certificationIds: to },
      };
    });
  };

  const importData = (input) => {
  try {
    const parsed = JSON.parse(input);
    if (!parsed.certifications) throw new Error();
    if (
      !checkColumnStructure(parsed.certifications, dndStructure.columns) ||
      !checkCertsStructure(parsed.certifications)
    ) {
      setError({ message: 'Wrong input file format!' });
      return;
    }

    parsed.certifications.forEach((item) => {
      const itemWithValidValues = verifyCertValues(item, {
        maxEcsf: dbECSFRoles.length,
        maxDomain: dbDomains.length,
        maxSkillGroup: rsSkillsGroup.length
      })
      const copy = { ...itemWithValidValues };
      const id = createDnDId(copy.name, 'import', Object.keys(certifications));
      copy.id = id;
      delete copy.position;

      setCertifications((prev) => ({
        ...prev,
        [id]: copy,
      }));

      setColumns((prev) => {
        const col = prev[itemWithValidValues.position];
        return {
          ...prev,
          [itemWithValidValues.position]: {
            ...col,
            certificationIds: [...col.certificationIds, id],
          },
        };
      });
    });

  } catch {
    setError({ message: 'Sorry, something went wrong!' });
  }
};

  const loadSamples = () => {
    if (!sampleCertifications?.length) {
      setError({ message: 'There are no sample certifications available!' });
      return;
    }
    const sorted = [...sampleCertifications].sort((a, b) => a.name.localeCompare(b.name));
    let newCols = { ...columns };
    let newCerts = { ...certifications };
    sorted.forEach((item) => {
      const copy = { ...item };
      copy.id = createDnDId(copy.name, 'sample', Object.keys(newCerts));
      newCols.available.certificationIds.push(copy.id);
      newCerts[copy.id] = copy;
    });
    setColumns(newCols);
    setCertifications(newCerts);
    setSampleLoaded(true);
  };

  const onDragStart = (start) => {
    setDraggedCertification(start.draggableId);
  };

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return setDraggedCertification(null);
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return setDraggedCertification(null);
    }
    const startCol = columns[source.droppableId];
    const endCol = columns[destination.droppableId];
    if (startCol === endCol) {
      const ids = Array.from(startCol.certificationIds);
      ids.splice(source.index, 1);
      ids.splice(destination.index, 0, draggableId);
      setColumns((prev) => ({ ...prev, [startCol.id]: { ...startCol, certificationIds: ids } }));
    } else {
      const startIds = Array.from(startCol.certificationIds);
      startIds.splice(source.index, 1);
      const finishIds = Array.from(endCol.certificationIds);
      finishIds.splice(destination.index, 0, draggableId);
      setColumns((prev) => ({
        ...prev,
        [startCol.id]: { ...startCol, certificationIds: startIds },
        [endCol.id]: { ...endCol, certificationIds: finishIds },
      }));
    }
    setDraggedCertification(null);
  };

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Alert message={error.message} type="error" showIcon />
        <Button type="link" onClick={() => setError(null)}>
          Go back
        </Button>
      </div>
    );
  }

  return (
    <Layout className="csprofiler-certs">
      <Content className="structure-content">
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <Row gutter={16}>
            <ListOfCertifications
              columns={columns}
              certifications={certifications}
              draggedCertification={draggedCertification}
              sampleCertificationsLoaded={sampleLoaded}
              addCustomCertification={addCustomCert}
              removeCertification={removeForeverCert}
              loadSamples={loadSamples}
              openAddModal={openAddModal}
              openBrowseModal={openBrowseModal}
            />
            <SelectedCertifications
              columns={columns}
              certifications={certifications}
              draggedCertification={draggedCertification}
              removeCertification={removeCert}
              importData={importData}
              openAddModal={openAddModal}
            />
            <Statistics rsSkillsGroup={rsSkillsGroup} rsProfiles={rsProfiles} certifications={columns.selected.certificationIds.map((id) => certifications[id])} />
          </Row>
        </DragDropContext>
      </Content>

      <CertificationCSPModalForm
        editRecord={addModalData}
        onCancel={closeAddModal}
        refresh={closeAddModal}
        saveLocally={submitModal}
        rsSkillsGroup={rsSkillsGroup}
        dbCertifications={dbCertifications}
        rsSpecificKnowledge={rsSpecificKnowledge}
        rsSpecificSkills={rsSpecificSkills}
        dbDomains={dbDomains}
        dbECSFRoles={dbECSFRoles}
      />

      <CertificationBrowseCSPModalForm
        open={showBrowseModal}
        onCancel={closeBrowseModal}
        onLoad={submitBrowse}
        dbCertifications={dbCertifications}
        rsSkillsGroup={rsSkillsGroup}
        rsSpecificKnowledge={rsSpecificKnowledge}
        rsSpecificSkills={rsSpecificSkills}
      />
    </Layout>
  );
};
export default Certifications;
